import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Plus, Check, Wrench, ChevronDown } from 'lucide-react';

/**
 * Pick condition-script phrases via a short-name dropdown + live preview.
 *
 * UX flow (per user spec):
 *   1. Roof-type filter narrows the list to phrases that match the
 *      property's roof (defaults to `defaultRoofType`).
 *   2. A "QuoteAppraisalCommentChoice" dropdown shows short names.
 *   3. Selecting a name renders the full script in a read-only preview.
 *   4. User stacks phrases by clicking "Add to selection" — each added
 *      phrase shows as a removable chip. "Insert" composes them into the
 *      appraisal_condition textarea (Append or Replace).
 */
export default function ConditionScriptPicker({ open, onClose, onInsert, defaultRoofType = '', inspectionCategory = '' }) {
  const { api } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [roofTypes, setRoofTypes] = useState([]);
  const [roofFilter, setRoofFilter] = useState(defaultRoofType || '');
  const [selectedScriptId, setSelectedScriptId] = useState('');
  const [picked, setPicked] = useState([]); // ordered list of script ids

  // Reload library each time the modal opens — admins may have edited
  // phrases between sessions; this keeps the picker in step.
  useEffect(() => {
    if (!open) return;
    // When the parent has scoped the picker to one inspection category,
    // hit the backend with that filter so the user only sees the relevant
    // phrases (e.g. Valleys-only picker shows the 10 Valleys phrases).
    const params = inspectionCategory ? { inspection_category: inspectionCategory } : {};
    Promise.all([
      api.get('/condition-scripts', { params }).then(r => Array.isArray(r.data) ? r.data : []),
      api.get('/condition-scripts/roof-types').then(r => Array.isArray(r.data) ? r.data : []),
    ]).then(([s, rt]) => {
      setScripts(s); setRoofTypes(rt);
    }).catch(() => { setScripts([]); setRoofTypes([]); });
    setRoofFilter(defaultRoofType || '');
    setPicked([]); setSelectedScriptId('');
  }, [open, api, defaultRoofType, inspectionCategory]);

  // Filter logic — match roof_type loosely (case-insensitive substring) and
  // always include generic scripts (no roof_type set). Mirrors the backend
  // /condition-scripts?roof_type=… behaviour for offline-friendly filtering.
  //
  // iter303ak-fix5 · Also check `roof_types` (plural, list) — 155 of 209
  // scripts are tagged via this array field (Long Run Iron, Modern Concrete,
  // Clay Tile, etc.) but the singular legacy field is only used on 8. Before
  // this fix, the picker treated the 155 as generic and showed everything.
  //
  // NOTE: useMemo must run unconditionally on every render — keep it BEFORE
  // any early `return null` to satisfy React's rules-of-hooks.
  const filtered = useMemo(() => {
    if (!roofFilter) return scripts;
    const needle = roofFilter.toLowerCase();
    return scripts.filter(s => {
      const rt = (s.roof_type || '').toLowerCase();
      const rts = Array.isArray(s.roof_types) ? s.roof_types : [];
      const trulyGeneric = !rt && rts.length === 0;
      if (trulyGeneric) return true;                                  // include generics
      if (rt && (rt.includes(needle) || needle.includes(rt))) return true;
      // Match any tag in the roof_types list (case-insensitive substring both ways)
      if (rts.some(x => {
        const t = String(x || '').toLowerCase();
        return t && (t.includes(needle) || needle.includes(t));
      })) return true;
      return false;
    });
  }, [scripts, roofFilter]);

  if (!open) return null;

  const previewScript = scripts.find(s => s._id === selectedScriptId);

  const addToSelection = () => {
    if (!selectedScriptId || picked.includes(selectedScriptId)) return;
    setPicked([...picked, selectedScriptId]);
    setSelectedScriptId('');
  };

  const removeFromSelection = (id) => setPicked(picked.filter(p => p !== id));

  const insert = (mode) => {
    const chosen = picked
      .map(id => scripts.find(s => s._id === id))
      .filter(Boolean);
    const text = chosen.map(s => s.body).filter(Boolean).join('\n\n');
    // Pass the FIRST picked script alongside the text so the parent
    // can auto-fill related fields (e.g. Roof Condition → roof-level
    // recommendation tag). We deliberately only surface one script here
    // — multi-pick tag merging isn't a real use-case yet.
    onInsert && onInsert(text, mode, chosen[0] || null);
    onClose && onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="condition-script-picker" className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#D35400]/10 flex items-center justify-center"><Wrench size={16} className="text-[#D35400]" /></div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">{inspectionCategory ? `Insert ${inspectionCategory} phrases` : 'Insert condition phrases'}</h2>
              <p className="text-[11px] text-slate-500">{inspectionCategory ? `Scoped to ${inspectionCategory} — narrow by roof type, preview, then add.` : 'Pick from the legacy Access library — narrow by roof type, preview, then add to your selection.'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"><X size={16} /></button>
        </div>

        {scripts.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No condition phrases in the library yet. Add some in Settings → Condition Scripts.</div>
        ) : (
          <>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              {/* Filters row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 block mb-1">Roof Type Filter</label>
                  <div className="relative">
                    <select
                      data-testid="picker-roof-filter"
                      value={roofFilter}
                      onChange={e => { setRoofFilter(e.target.value); setSelectedScriptId(''); }}
                      className="w-full appearance-none pl-3 pr-9 py-2 border border-[#E2E2DF] rounded-lg text-sm bg-white focus:outline-none focus:border-[#D35400]"
                    >
                      <option value="">All roof types ({scripts.length})</option>
                      {roofTypes.map(rt => {
                        // iter303ak-fix5 · Mirror the filter's dual-field
                        // logic so the (N) count matches the actual matched
                        // rows. Previously only looked at s.roof_type
                        // (singular) which under-counted by 90%.
                        const needle = rt.toLowerCase();
                        const count = scripts.filter(s => {
                          const rti = (s.roof_type || '').toLowerCase();
                          const rts = Array.isArray(s.roof_types) ? s.roof_types : [];
                          if (!rti && rts.length === 0) return true;                    // generic
                          if (rti && (rti.includes(needle) || needle.includes(rti))) return true;
                          return rts.some(x => {
                            const t = String(x || '').toLowerCase();
                            return t && (t.includes(needle) || needle.includes(t));
                          });
                        }).length;
                        return <option key={rt} value={rt}>{rt} ({count})</option>;
                      })}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 block mb-1">QuoteAppraisalComment Choice <span className="text-slate-400 normal-case font-normal">— short name</span></label>
                  <div className="relative">
                    <select
                      data-testid="picker-name-select"
                      value={selectedScriptId}
                      onChange={e => setSelectedScriptId(e.target.value)}
                      className="w-full appearance-none pl-3 pr-9 py-2 border border-[#E2E2DF] rounded-lg text-sm bg-white focus:outline-none focus:border-[#D35400]"
                    >
                      <option value="">— pick a phrase —</option>
                      {filtered.map(s => (
                        <option key={s._id} value={s._id} disabled={picked.includes(s._id)}>
                          {s.name}{s.roof_type ? ` · ${s.roof_type}` : ''}{picked.includes(s._id) ? ' (added)' : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Preview window */}
              <div className="border border-[#E2E2DF] rounded-md p-4 bg-slate-50/60 min-h-[170px]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Preview</span>
                  {previewScript && (
                    <button
                      type="button"
                      data-testid="picker-add-current-btn"
                      onClick={addToSelection}
                      className="flex items-center gap-1 px-2.5 py-1 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-[11px] font-bold uppercase tracking-[0.12em]"
                    >
                      <Plus size={12} strokeWidth={2.5} /> Add to selection
                    </button>
                  )}
                </div>
                {!previewScript ? (
                  <p className="text-sm text-slate-400 italic">Pick a phrase from the dropdown above to preview the full script here.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800">{previewScript.name}</span>
                      {previewScript.roof_type && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#A04000] bg-[#FFF7ED] border border-[#D35400]/30 rounded-full px-2 py-0.5">{previewScript.roof_type}</span>}
                      {previewScript.recommendation && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{previewScript.recommendation}</span>}
                    </div>
                    <pre data-testid="picker-preview-body" className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{previewScript.body}</pre>
                  </div>
                )}
              </div>

              {/* Picked chips */}
              {picked.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Selected for insertion ({picked.length})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {picked.map((id, idx) => {
                      const s = scripts.find(x => x._id === id);
                      if (!s) return null;
                      return (
                        <span key={id} data-testid={`picker-chip-${id}`} className="inline-flex items-center gap-1.5 bg-[#FFF7ED] border border-[#D35400]/30 text-[#A04000] text-xs font-semibold rounded-full pl-2.5 pr-1 py-1">
                          <span className="font-mono-num text-[10px] text-slate-500">{idx + 1}.</span>
                          {s.name}
                          <button onClick={() => removeFromSelection(id)} className="p-0.5 rounded-full hover:bg-[#D35400]/15" aria-label={`Remove ${s.name}`}><X size={11} strokeWidth={2.5} /></button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 p-4 border-t border-slate-200 bg-slate-50/50">
              <span className="text-xs text-slate-500 font-mono-num">{filtered.length} phrase{filtered.length === 1 ? '' : 's'} {roofFilter ? `matching "${roofFilter}"` : 'available'}</span>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-white font-medium">Cancel</button>
                <button
                  data-testid="picker-replace-btn"
                  onClick={() => insert('replace')}
                  disabled={picked.length === 0}
                  className="px-4 py-2 border border-[#D35400] text-[#D35400] hover:bg-[#FFF7ED] rounded-lg text-sm font-semibold disabled:opacity-40"
                >
                  Replace existing
                </button>
                <button
                  data-testid="picker-append-btn"
                  onClick={() => insert('append')}
                  disabled={picked.length === 0}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000] disabled:opacity-40"
                >
                  <Check size={14} strokeWidth={2.5} /> Insert ({picked.length})
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
